/**
 * PigeonNotifier — TTS notification engine for hub live feed events.
 * Reads SHORT nature of notifications aloud (no details).
 * Supports 9 languages via browser SpeechSynthesis.
 */

// Map event_type to SHORT spoken phrase per language
const SPOKEN_MAP = {
  en: {
    SHIFT_START: "Shift started",
    SHIFT_STOP: "Shift ended",
    PLANNER_APPROVAL: "Request approved",
    DRIVER_DELIVERY_COMPLETE: "Delivery complete",
    DRIVER_BREAK: "Driver on break",
    DRIVER_BREAKDOWN: "Driver breakdown",
    DRIVER_FEEDBACK: "Driver feedback",
    MACHINE_ALERT: "Machine alert",
    MACHINE_STATUS_CHANGE: "Machine status change",
    MACHINE_DEFECT: "Machine defect",
    MACHINE_MAINTENANCE: "Maintenance request",
    TRANSPORT_REQUEST: "Transport request",
    TRANSPORT_CONFIRMED: "Transport confirmed",
    DELIVERY_SUCCESS: "Delivery success",
    DELIVERY_INCIDENT: "Delivery incident",
    SITE_UPDATE: "Site updated",
    LIKE_ACTION: "High five",
    REQUEST_CREATED: "New request",
    MEMBER_JOINED: "New member joined",
    MACHINE_ADDED: "New machine added",
    SITE_ADDED: "New site added",
  },
  nl: {
    SHIFT_START: "Dienst gestart",
    SHIFT_STOP: "Dienst beëindigd",
    PLANNER_APPROVAL: "Aanvraag goedgekeurd",
    DRIVER_DELIVERY_COMPLETE: "Levering compleet",
    DRIVER_BREAK: "Chauffeur pauze",
    DRIVER_BREAKDOWN: "Chauffeur panne",
    DRIVER_FEEDBACK: "Chauffeur feedback",
    MACHINE_ALERT: "Machine alarm",
    MACHINE_STATUS_CHANGE: "Machine status",
    MACHINE_DEFECT: "Machine defect",
    MACHINE_MAINTENANCE: "Onderhoudsaanvraag",
    TRANSPORT_REQUEST: "Transportaanvraag",
    TRANSPORT_CONFIRMED: "Transport bevestigd",
    DELIVERY_SUCCESS: "Levering geslaagd",
    DELIVERY_INCIDENT: "Leveringsincident",
    SITE_UPDATE: "Werf bijgewerkt",
    LIKE_ACTION: "High five",
    REQUEST_CREATED: "Nieuwe aanvraag",
    MEMBER_JOINED: "Nieuw lid",
    MACHINE_ADDED: "Nieuwe machine",
    SITE_ADDED: "Nieuwe werf",
  },
  fr: {
    SHIFT_START: "Quart commencé",
    SHIFT_STOP: "Quart terminé",
    PLANNER_APPROVAL: "Demande approuvée",
    DRIVER_DELIVERY_COMPLETE: "Livraison terminée",
    REQUEST_CREATED: "Nouvelle demande",
    MEMBER_JOINED: "Nouveau membre",
    MACHINE_ADDED: "Nouvelle machine",
    SITE_ADDED: "Nouveau site",
  },
  de: {
    SHIFT_START: "Schicht gestartet",
    SHIFT_STOP: "Schicht beendet",
    PLANNER_APPROVAL: "Anfrage genehmigt",
    DRIVER_DELIVERY_COMPLETE: "Lieferung abgeschlossen",
    REQUEST_CREATED: "Neue Anfrage",
    MEMBER_JOINED: "Neues Mitglied",
    MACHINE_ADDED: "Neue Maschine",
    SITE_ADDED: "Neue Baustelle",
  },
};

const LANG_VOICE_MAP = {
  en: "en-US", nl: "nl-NL", fr: "fr-FR", de: "de-DE",
  pl: "pl-PL", ro: "ro-RO", ar: "ar-SA", zh: "zh-CN", it: "it-IT",
};

class PigeonNotifierEngine {
  constructor() {
    this.enabled = false;
    this.lang = "en";
    this.queue = [];
    this.speaking = false;
    this.seenIds = new Set();
    this.onNotification = null; // callback for UI badge
  }

  setLang(lang) {
    this.lang = lang || "en";
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      window.speechSynthesis?.cancel();
      this.queue = [];
      this.speaking = false;
    }
  }

  getSpokenText(eventType) {
    const langMap = SPOKEN_MAP[this.lang] || SPOKEN_MAP.en;
    return langMap[eventType] || SPOKEN_MAP.en[eventType] || eventType?.replace(/_/g, " ")?.toLowerCase();
  }

  processEvents(events) {
    if (!events || events.length === 0) return;

    const newEvents = [];
    for (const ev of events) {
      const id = ev.id || `${ev.event_type}-${ev.timestamp}`;
      if (!this.seenIds.has(id)) {
        this.seenIds.add(id);
        newEvents.push(ev);
      }
    }

    // Keep seenIds bounded
    if (this.seenIds.size > 500) {
      const arr = [...this.seenIds];
      this.seenIds = new Set(arr.slice(-300));
    }

    if (newEvents.length === 0) return;

    // Notify UI
    if (this.onNotification) {
      this.onNotification(newEvents);
    }

    // Queue spoken notifications
    if (this.enabled && window.speechSynthesis) {
      for (const ev of newEvents) {
        const text = this.getSpokenText(ev.event_type);
        this.queue.push(text);
      }
      this._processQueue();
    }
  }

  _processQueue() {
    if (this.speaking || this.queue.length === 0) return;
    this.speaking = true;

    const text = this.queue.shift();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = LANG_VOICE_MAP[this.lang] || "en-US";
    utterance.rate = 1.1;
    utterance.pitch = 1.0;
    utterance.volume = 0.8;

    utterance.onend = () => {
      this.speaking = false;
      // Small gap between announcements
      setTimeout(() => this._processQueue(), 400);
    };
    utterance.onerror = () => {
      this.speaking = false;
      setTimeout(() => this._processQueue(), 200);
    };

    window.speechSynthesis.speak(utterance);
  }

  // Immediately speak a one-off message (for confirmations etc.)
  speakNow(text) {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = LANG_VOICE_MAP[this.lang] || "en-US";
    utterance.rate = 1.0;
    utterance.volume = 0.9;
    window.speechSynthesis.speak(utterance);
  }
}

// Singleton
const pigeonNotifier = new PigeonNotifierEngine();
export default pigeonNotifier;
